// analytics — a thin, safe wrapper over PostHog so product code can fire semantic
// events without caring whether analytics is configured. Every call no-ops when
// PostHog isn't initialised (no NEXT_PUBLIC_POSTHOG_KEY) or on the server, so an
// event can never break a user flow. PostHog itself is initialised once in
// instrumentation-client.ts; here we only *use* it.
//
// This is the BROWSER half. Its server twin is lib/analytics/server.ts, and both
// speak the one taxonomy in lib/analytics/events.ts — that shared vocabulary is
// what lets a funnel span a dashboard click and an MCP tool call. Prefer
// `trackEvent` / `trackFunnel` over raw `track`: they take a typed name, so a
// misspelling is a build failure rather than a hole in a report nobody notices
// for a month. `track` stays exported and untyped for one-off names that don't
// belong in the taxonomy (e.g. the waitlist's own event).

import posthog from "posthog-js";
import {
  createTypedTrack,
  funnelProps,
  type AnalyticsEvent,
  type AnalyticsProperties,
  type TypedTrack,
} from "./analytics/events";
import { templatePlacePath } from "./analytics/placePath";
import { readUtm } from "./utm";

export function track(event: string, properties?: Record<string, unknown>) {
  try {
    if (typeof window !== "undefined" && posthog.__loaded) {
      posthog.capture(event, properties);
    }
  } catch {
    /* analytics must never throw into a user flow */
  }
}

/**
 * `track`, but the event has to survive the page dying.
 *
 * PostHog BATCHES captures and flushes on an interval. Its own unload handler is
 * registered inside `posthog.init()` — before any listener of ours exists — so
 * on `pagehide` it fires `$pageleave`, flushes the queue, and only THEN do our
 * handlers run. Anything captured in that window is enqueued into a queue that
 * has already left, and is never sent.
 *
 * Measured on the investor deck (2026-09-14): 29 sessions fired `$pageleave`,
 * only 7 ever fired `deck_finished`. Those 7 averaged 11.6 pageviews against
 * 1.6 for the other 48 — i.e. the event only ever survived when the reader
 * navigated onward, never when they closed the tab, which is what a reader
 * finishing a deck actually does.
 *
 * `sendBeacon` hands the request to the browser synchronously at capture time,
 * so it does not care whether a flush already happened or whether the document
 * is being torn down. Use this — not `track` — for anything fired from
 * `pagehide`, from `visibilitychange` going hidden, or from an unmount that may
 * be the page's last moment. Everything mid-visit should stay on `track`, which
 * batches and costs one request instead of twenty.
 */
export function trackBeacon(event: string, properties?: Record<string, unknown>) {
  try {
    if (typeof window !== "undefined" && posthog.__loaded) {
      posthog.capture(event, properties, { transport: "sendBeacon" });
    }
  } catch {
    /* analytics must never throw into a user flow */
  }
}

/** Tie subsequent events to a known person (call after sign-in). No-op if unset. */
export function identify(distinctId: string, properties?: Record<string, unknown>) {
  try {
    if (typeof window !== "undefined" && posthog.__loaded) {
      posthog.identify(distinctId, properties);
    }
  } catch {
    /* ignore */
  }
}

/** Clear identity on sign-out so the next visitor isn't stitched to this person. */
export function resetAnalytics() {
  try {
    if (typeof window !== "undefined" && posthog.__loaded) posthog.reset();
  } catch {
    /* ignore */
  }
}

/**
 * `track`, but the event name must come from the taxonomy. Same behaviour, same
 * no-op guarantees — the only difference is that the compiler now knows which
 * names exist, and which of them require properties.
 */
export const trackEvent: TypedTrack = createTypedTrack(track);

/**
 * `trackEvent`, sent by beacon so it survives the page unloading.
 *
 * Same taxonomy, same no-op guarantees — see `trackBeacon` for why the ordinary
 * path loses an event fired on the way out.
 */
export const trackExit: TypedTrack = createTypedTrack(trackBeacon);

/**
 * Record a step of the user journey with the properties every funnel needs.
 *
 * The point is CONSISTENCY, not convenience. Three things get stamped on every
 * call so a report can slice by them without each call site remembering to:
 *
 *   • `funnel_stage` — which of the six stages this event belongs to, read from
 *     the taxonomy rather than passed in, so it can never disagree with it;
 *   • `surface` — the pathname the step happened on, which is how you tell the
 *     connect flow reached from onboarding apart from the one reached from
 *     settings. A place on the stage is its TEMPLATE, never the title-slug in
 *     its address (lib/analytics/placePath);
 *   • first-touch `utm_*` — so drop-off at any step can be broken down by the
 *     campaign that brought the person in, not just the campaign that happened
 *     to be in the URL at the moment they converted.
 *
 * `emitted_from` separates these from their server twins (lib/analytics/server.ts
 * stamps `'server'`), because "the browser never saw it" is usually the answer
 * when a step looks impossibly fast.
 *
 * Explicit properties win over all of it — the stamps are defaults, not law.
 */
export const trackFunnel: TypedTrack = createTypedTrack(
  (event: string, properties?: AnalyticsProperties) => {
    let surface: string | undefined;
    try {
      surface = typeof window !== "undefined" ? templatePlacePath(window.location.pathname) : undefined;
    } catch {
      /* ignore — a missing surface is not worth losing the event over */
    }
    track(event, {
      ...readUtm(),
      surface,
      emitted_from: "browser",
      ...funnelProps(event as AnalyticsEvent, properties),
    });
  },
);

export { posthog };
export { EVENTS, type AnalyticsEvent, type FunnelStage } from "./analytics/events";
